// pages/TranslatorDashboard.jsx with project functionality and translation history
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

// Services
import translationService from '../services/translationService';
import useApiClient, { translationApi } from '../services/api';

// Components
import Spinner from '../components/Spinner';
import ConfirmDialog from '../components/ConfirmDialog';

// Constants
import { getLanguageName } from '../constants/languages';

const APPROVAL_LIFECYCLE_STATUSES = new Set(['PENDING', 'APPROVED', 'CHANGES_REQUESTED', 'EXPIRED']);
const PENDING_CLIENT_APPROVAL_STATUSES = new Set(['PENDING', 'CHANGES_REQUESTED']);
const PROJECT_DESCRIPTION_PREVIEW_MAX_LENGTH = 30;
const getDefaultFilters = () => ({
  sourceLanguage: '',
  targetLanguage: '',
  status: '',
  projectStatus: '',
  clientName: '',
  projectManager: '',
  approvalStatus: '',
  createdDateMode: 'day',
  createdDateDay: '',
  createdDateMonth: '',
  createdDateFrom: '',
  createdDateTo: '',
  dueDateMode: 'day',
  dueDateDay: '',
  dueDateMonth: '',
  dueDateFrom: '',
  dueDateTo: ''
});

const TranslatorDashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [translations, setTranslations] = useState([]);
  const [projects, setProjects] = useState([]);
  const [latestApprovalStatusByTranslationId, setLatestApprovalStatusByTranslationId] = useState({});
  const [loading, setLoading] = useState(true);
  const [claimingId, setClaimingId] = useState(null);
  const [activeTab, setActiveTab] = useState('history'); // 'history' or 'available'
  const [filters, setFilters] = useState(getDefaultFilters());
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    translationId: null,
    translationName: ''
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteProjectDialog, setDeleteProjectDialog] = useState({
    isOpen: false,
    projectId: null,
    projectName: ''
  });
  const [isDeletingProject, setIsDeletingProject] = useState(false);
  const [expandedProjectDescriptions, setExpandedProjectDescriptions] = useState({});
  const [isFiltersPopoverOpen, setIsFiltersPopoverOpen] = useState(false);
  const hasFetched = useRef(false);
  const filtersPopoverRef = useRef(null);

  const apiClient = useApiClient();
  // Fetch user's translations and projects
  useEffect(() => {
    if (!apiClient || hasFetched.current) return;
    hasFetched.current = true;

    const fetchProjectApprovalStatuses = async (projectList) => {
      const projectIds = (projectList || []).map((project) => project.id).filter(Boolean);

      if (projectIds.length === 0) {
        setLatestApprovalStatusByTranslationId({});
        return;
      }

      const approvalResults = await Promise.allSettled(
        projectIds.map((projectId) => apiClient.get(`/approvals/project/${projectId}`))
      );

      const statusMap = {};

      approvalResults.forEach((result, index) => {
        const projectId = projectIds[index];

        if (result.status !== 'fulfilled') {
          console.error('Failed to fetch approvals for project:', projectId, result.reason);
          return;
        }

        if (!result.value?.data?.success) {
          console.warn('Approvals request returned unsuccessful response for project:', projectId);
          return;
        }

        const sortedRequests = [...(result.value.data.approvalRequests || [])].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );

        sortedRequests.forEach((request) => {
          (request.translationApprovals || []).forEach((approval) => {
            if (!approval?.translationId || !approval?.status) {
              return;
            }

            if (!statusMap[approval.translationId]) {
              statusMap[approval.translationId] = approval.status;
            }
          });
        });
      });

      setLatestApprovalStatusByTranslationId(statusMap);
    };

    const fetchData = async () => {
      try {
        setLoading(true);
        
        const [projectsResponse, translationsResponse] = await Promise.all([
          apiClient.get('/projects'),
          apiClient.get('/translations')
        ]);

        setProjects(projectsResponse.data.projects);
        setTranslations(translationsResponse.data.translations);

        // Fetch approvals after base dashboard data so project cards render without waiting.
        fetchProjectApprovalStatuses(projectsResponse.data.projects || []).catch((error) => {
          console.error('Error fetching project approvals:', error);
        });
      } catch (error) {
        console.error('Error fetching data:', error);
        const isAuthError = error.response?.status === 401 || error.response?.status === 403;
        if (isAuthError) {
          toast.error(t('dashboard.messages.sessionExpired'));
        } else {
          toast.error(t('dashboard.messages.loadDataFailed'));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [apiClient]);

  useEffect(() => {
    if (!isFiltersPopoverOpen) {
      return undefined;
    }

    const handleOutsideClick = (event) => {
      if (filtersPopoverRef.current && !filtersPopoverRef.current.contains(event.target)) {
        setIsFiltersPopoverOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isFiltersPopoverOpen]);

  // Handle claiming a translation
  const handleClaimTranslation = async (translationId) => {
    try {
      setClaimingId(translationId);
      
      // Use the translation service to claim the translation
      await translationService(apiClient).claimTranslation(translationId);
      
      // Update local state to reflect the change
      setTranslations(prevTranslations =>
        prevTranslations.map(translation => 
          translation.id === translationId 
            ? { ...translation, status: 'claimed', assignedTranslator: 'current_user' } 
            : translation
        )
      );
      
      toast.success(t('dashboard.messages.claimSuccess'));

      // Redirect to the translation setup page
      navigate(`/translator/setup/${translationId}`);
    } catch (error) {
      console.error('Error claiming translation:', error);
      toast.error(t('dashboard.messages.claimFailed'));
    } finally {
      setClaimingId(null);
    }
  };

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearAllFilters = () => {
    setFilters(getDefaultFilters());
  };

  const clearAdvancedFilter = (filterKey) => {
    setFilters((prev) => {
      if (filterKey === 'projectStatus') {
        return { ...prev, projectStatus: '' };
      }

      if (filterKey === 'status') {
        return { ...prev, status: '' };
      }

      if (filterKey === 'clientName') {
        return { ...prev, clientName: '' };
      }

      if (filterKey === 'projectManager') {
        return { ...prev, projectManager: '' };
      }

      if (filterKey === 'createdDate') {
        return {
          ...prev,
          createdDateMode: 'day',
          createdDateDay: '',
          createdDateMonth: '',
          createdDateFrom: '',
          createdDateTo: ''
        };
      }

      if (filterKey === 'dueDate') {
        return {
          ...prev,
          dueDateMode: 'day',
          dueDateDay: '',
          dueDateMonth: '',
          dueDateFrom: '',
          dueDateTo: ''
        };
      }

      return prev;
    });
  };

  // Navigate to translation setup page for new translation (without project)
  const handleCreateNewTranslation = () => {
    navigate('/translator/setup');
  };

  // Navigate to create new project page
  const handleCreateNewProject = () => {
    navigate('/translator/project/create');
  };

  const handleImportTranslation = () => {
    navigate('/translator/import');
  };

  // Handle translation row click based on status
  const handleTranslationRowClick = (translation) => {
    const projectId = translation.project?.id || translation.projectId;

    if (translation.status === 'PENDING') {
      // Navigate to setup/start translation with project ID
      const setupPath = projectId
        ? `/translator/setup/${translation.documentId}?projectId=${projectId}`
        : `/translator/setup/${translation.documentId}`;
      navigate(setupPath);
    } else if (translation.status === 'COMPLETED') {
      // Navigate to edit translation (Google Docs mode)
      navigate(`/translator/edit/${translation.documentId}?mode=googledocs`);
    }
    // For other statuses (QUEUE, PROCESSING), do nothing
  };

  const getDueDateStatus = (project) => {
    if (!project.dueDate) return null;
    
    const now = new Date();
    const dueDate = new Date(project.dueDate);
    const diffTime = dueDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    // Check if all translations for this project are completed
    const projectTranslations = translations.filter(t => t.projectId === project.id);
    const allCompleted = projectTranslations.length > 0 && projectTranslations.every(t => t.status === 'COMPLETED');
    
    if (allCompleted) {
      return { status: 'completed', className: 'text-green-600', message: t('dashboard.status.completed') };
    }

    if (diffDays < 0) {
      return { status: 'overdue', className: 'text-red-600 font-semibold', message: t('dashboard.status.overdue') };
    } else if (diffDays <= 2) {
      return { status: 'urgent', className: 'text-red-500 font-semibold', message: t('dashboard.status.dueSoon') };
    } else if (diffDays <= 7) {
      return { status: 'warning', className: 'text-orange-500', message: t('dashboard.status.dueToday') };
    }
    
    return { status: 'normal', className: 'text-gray-700', message: null };
  };

  const getProjectCompletionStatus = (project) => {
    const projectTranslations = translations.filter(t => t.projectId === project.id);
    const completedCount = projectTranslations.filter(t => t.status === 'COMPLETED').length;
    const totalCount = projectTranslations.length;

    return {
      completed: completedCount,
      total: totalCount,
      percentage: totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0
    };
  };

  const getReadyForApprovalStatus = (project) => {
    const projectTranslations = translations.filter(t => t.projectId === project.id);
    const readyCount = projectTranslations.filter((translation) => {
      const isReadyForApproval = translation.readyForApproval === true || translation.ready_for_approval === true;
      const latestApprovalStatus = latestApprovalStatusByTranslationId[translation.id];
      const hasApprovalLifecycleStatus = APPROVAL_LIFECYCLE_STATUSES.has(latestApprovalStatus);

      return isReadyForApproval && !hasApprovalLifecycleStatus;
    }).length;
    const totalCount = projectTranslations.length;

    return {
      ready: readyCount,
      total: totalCount,
      percentage: totalCount > 0 ? Math.round((readyCount / totalCount) * 100) : 0
    };
  };

  const getProjectDescriptionPreview = (description) => {
    const normalizedDescription = (description || '').replace(/\r\n/g, '\n');
    const descriptionLines = normalizedDescription.split('\n');
    const firstLine = descriptionLines[0] || '';
    const hasAdditionalLines = descriptionLines.length > 1;
    const previewText = firstLine.slice(0, PROJECT_DESCRIPTION_PREVIEW_MAX_LENGTH);
    const isTruncated = hasAdditionalLines || firstLine.length > PROJECT_DESCRIPTION_PREVIEW_MAX_LENGTH;

    return { previewText, isTruncated };
  };

  const normalizeFilterText = (value) => (value || '').trim().toLowerCase();

  const formatLocalDate = (value) => {
    if (!value) return null;

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;

    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isDateFilterActive = (mode, dayValue, monthValue, fromValue, toValue) => {
    if (mode === 'day') return Boolean(dayValue);
    if (mode === 'month') return Boolean(monthValue);
    return Boolean(fromValue || toValue);
  };

  const matchesDateFilter = (dateValue, mode, dayValue, monthValue, fromValue, toValue) => {
    const hasActiveDateFilter = isDateFilterActive(mode, dayValue, monthValue, fromValue, toValue);

    if (!hasActiveDateFilter) {
      return true;
    }

    const localDate = formatLocalDate(dateValue);
    if (!localDate) {
      return false;
    }

    if (mode === 'day') {
      return localDate === dayValue;
    }

    if (mode === 'month') {
      return localDate.slice(0, 7) === monthValue;
    }

    if (fromValue && localDate < fromValue) {
      return false;
    }

    if (toValue && localDate > toValue) {
      return false;
    }

    return true;
  };

  const toggleProjectDescription = (projectId, event) => {
    event.preventDefault();
    event.stopPropagation();
    setExpandedProjectDescriptions((prev) => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  const getProjectApprovalSummary = (project) => {
    const projectTranslations = translations.filter(t => t.projectId === project.id);
    const readyForApprovalStatus = getReadyForApprovalStatus(project);

    let hasPendingClientApproval = false;
    let hasChangesRequested = false;

    projectTranslations.forEach((translation) => {
      const latestApprovalStatus = latestApprovalStatusByTranslationId[translation.id];

      if (latestApprovalStatus === 'CHANGES_REQUESTED') {
        hasChangesRequested = true;
      }

      if (PENDING_CLIENT_APPROVAL_STATUSES.has(latestApprovalStatus)) {
        hasPendingClientApproval = true;
      }
    });

    return {
      hasPendingSendingForApproval: readyForApprovalStatus.ready > 0,
      hasPendingClientApproval,
      hasChangesRequested
    };
  };

  const createdDateFilterActive = isDateFilterActive(
    filters.createdDateMode,
    filters.createdDateDay,
    filters.createdDateMonth,
    filters.createdDateFrom,
    filters.createdDateTo
  );

  const dueDateFilterActive = isDateFilterActive(
    filters.dueDateMode,
    filters.dueDateDay,
    filters.dueDateMonth,
    filters.dueDateFrom,
    filters.dueDateTo
  );

  const hasProjectLevelFiltersActive = Boolean(
    filters.projectStatus ||
    filters.approvalStatus ||
    normalizeFilterText(filters.clientName) ||
    normalizeFilterText(filters.projectManager) ||
    createdDateFilterActive ||
    dueDateFilterActive
  );

  const compactControlClassName = 'h-8 px-2 text-sm border border-gray-300 rounded-full bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';
  const popoverControlClassName = 'h-9 px-3 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';

  const getDateFilterSummary = (prefix) => {
    const mode = filters[`${prefix}Mode`];
    const day = filters[`${prefix}Day`];
    const month = filters[`${prefix}Month`];
    const from = filters[`${prefix}From`];
    const to = filters[`${prefix}To`];

    if (mode === 'day' && day) {
      return `${t('dashboard.filters.byDay')}: ${day}`;
    }

    if (mode === 'month' && month) {
      return `${t('dashboard.filters.byMonth')}: ${month}`;
    }

    if (mode === 'custom' && (from || to)) {
      return `${t('dashboard.filters.customPeriod')}: ${from || '...'} → ${to || '...'}`;
    }

    return null;
  };

  const activeAdvancedFilters = [
    filters.projectStatus
      ? { key: 'projectStatus', label: t('dashboard.filters.projectStatus'), value: filters.projectStatus }
      : null,
    filters.status
      ? { key: 'status', label: t('dashboard.filters.status'), value: filters.status }
      : null,
    normalizeFilterText(filters.clientName)
      ? { key: 'clientName', label: t('dashboard.filters.clientName'), value: filters.clientName.trim() }
      : null,
    createdDateFilterActive
      ? { key: 'createdDate', label: t('dashboard.filters.createdDate'), value: getDateFilterSummary('createdDate') }
      : null,
    dueDateFilterActive
      ? { key: 'dueDate', label: t('dashboard.filters.dueDateFilter'), value: getDateFilterSummary('dueDate') }
      : null
  ].filter(Boolean);

  const activeAdvancedFilterCount = activeAdvancedFilters.length;
  const quickFilterContainerClass = (isActive) => (
    `inline-flex items-center gap-2 shrink-0 rounded-full border px-2 py-1 transition-colors ${
      isActive ? 'border-green-400 bg-green-300' : 'border-gray-300 bg-white'
    }`
  );

  const matchesProjectFilters = (project) => {
    if (!project) {
      return false;
    }

    if (filters.projectStatus) {
      const dueDateStatus = getDueDateStatus(project);

      if (filters.projectStatus === 'completed' && dueDateStatus?.status !== 'completed') {
        return false;
      }

      if (filters.projectStatus === 'overdue' && dueDateStatus?.status !== 'overdue') {
        return false;
      }
    }

    const normalizedClientNameFilter = normalizeFilterText(filters.clientName);
    if (normalizedClientNameFilter) {
      const projectClientName = normalizeFilterText(project.clientName);
      if (!projectClientName.includes(normalizedClientNameFilter)) {
        return false;
      }
    }

    const normalizedProjectManagerFilter = normalizeFilterText(filters.projectManager);
    if (normalizedProjectManagerFilter) {
      const projectManagerName = normalizeFilterText(project.translatorName);
      if (!projectManagerName.includes(normalizedProjectManagerFilter)) {
        return false;
      }
    }

    if (!matchesDateFilter(
      project.createdAt,
      filters.createdDateMode,
      filters.createdDateDay,
      filters.createdDateMonth,
      filters.createdDateFrom,
      filters.createdDateTo
    )) {
      return false;
    }

    if (!matchesDateFilter(
      project.dueDate,
      filters.dueDateMode,
      filters.dueDateDay,
      filters.dueDateMonth,
      filters.dueDateFrom,
      filters.dueDateTo
    )) {
      return false;
    }

    if (filters.approvalStatus) {
      const projectApprovalSummary = getProjectApprovalSummary(project);

      if (filters.approvalStatus === 'pending_sending_for_approval' && !projectApprovalSummary.hasPendingSendingForApproval) {
        return false;
      }

      if (filters.approvalStatus === 'pending_client_approval' && !projectApprovalSummary.hasPendingClientApproval) {
        return false;
      }

      if (filters.approvalStatus === 'changes_requested' && !projectApprovalSummary.hasChangesRequested) {
        return false;
      }
    }

    return true;
  };

  const projectsById = projects.reduce((map, project) => {
    map[project.id] = project;
    return map;
  }, {});

  // Apply filters to projects
  const filteredProjects = projects.filter(project => {
    // Filter by source language
    if (filters.sourceLanguage && project.sourceLanguage !== filters.sourceLanguage) {
      return false;
    }
    // Filter by target language
    if (filters.targetLanguage && project.targetLanguage !== filters.targetLanguage) {
      return false;
    }

    return matchesProjectFilters(project);
  });

  // Apply filters to translations
  const filteredTranslations = translations.filter(translation => {
    // Filter by source language
    if (filters.sourceLanguage && translation.sourceLanguage !== filters.sourceLanguage) {
      return false;
    }
    // Filter by target language
    if (filters.targetLanguage && translation.targetLanguage !== filters.targetLanguage) {
      return false;
    }
    // Filter by status
    if (filters.status && translation.status !== filters.status) {
      return false;
    }

    if (hasProjectLevelFiltersActive) {
      const projectId = translation.projectId || translation.project?.id;
      const project = projectId ? projectsById[projectId] : null;

      if (!project) {
        return false;
      }

      if (!matchesProjectFilters(project)) {
        return false;
      }
    }

    return true;
  });

  // Handle tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'available') {
      setFilters(prev => ({ ...prev, status: 'PENDING' }));
    } else {
      setFilters(prev => ({ ...prev, status: '' }));
    }
  };

  // Handle delete translation
  const handleDeleteTranslation = (translation, e) => {
    e.stopPropagation();
    setDeleteDialog({
      isOpen: true,
      translationId: translation.documentId,
      translationName: translation.originalFileName || 'this translation'
    });
  };

  const confirmDeleteTranslation = async () => {
    try {
      setIsDeleting(true);
      await translationApi.deleteTranslation(apiClient, deleteDialog.translationId);

      toast.success(t('dashboard.messages.deleteSuccess'));

      // Remove translation from local state
      setTranslations(prevTranslations =>
        prevTranslations.filter(t => t.documentId !== deleteDialog.translationId)
      );

      // Close dialog
      setDeleteDialog({ isOpen: false, translationId: null, translationName: '' });
    } catch (error) {
      console.error('Error deleting translation:', error);
      toast.error(t('dashboard.messages.deleteFailed'));
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDeleteTranslation = () => {
    setDeleteDialog({ isOpen: false, translationId: null, translationName: '' });
  };

  // Handle delete project
  const handleDeleteProject = (project, e) => {
    e.preventDefault();    // Prevent anchor navigation
    e.stopPropagation();   // Prevent event bubbling
    setDeleteProjectDialog({
      isOpen: true,
      projectId: project.id,
      projectName: project.name
    });
  };

  const confirmDeleteProject = async () => {
    try {
      setIsDeletingProject(true);
      await apiClient.delete(`/projects/${deleteProjectDialog.projectId}`);

      toast.success(t('dashboard.messages.deleteProjectSuccess'));

      // Remove project from local state
      setProjects(prevProjects =>
        prevProjects.filter(p => p.id !== deleteProjectDialog.projectId)
      );

      // Also remove all translations associated with this project
      setTranslations(prevTranslations =>
        prevTranslations.filter(t => t.projectId !== deleteProjectDialog.projectId)
      );

      // Close dialog
      setDeleteProjectDialog({ isOpen: false, projectId: null, projectName: '' });
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error(t('dashboard.messages.deleteProjectFailed'));
    } finally {
      setIsDeletingProject(false);
    }
  };

  const cancelDeleteProject = () => {
    setDeleteProjectDialog({ isOpen: false, projectId: null, projectName: '' });
  };

  const renderDateFilterMenuControl = (prefix, labelKey) => {
    const modeName = `${prefix}Mode`;
    const dayName = `${prefix}Day`;
    const monthName = `${prefix}Month`;
    const fromName = `${prefix}From`;
    const toName = `${prefix}To`;
    const modeValue = filters[modeName];

    return (
      <div className="space-y-2">
        <label className="block text-xs font-medium text-gray-600">{t(labelKey)}</label>
        <select
          name={modeName}
          value={modeValue}
          onChange={handleFilterChange}
          className={`${popoverControlClassName} w-full`}
        >
          <option value="day">{t('dashboard.filters.byDay')}</option>
          <option value="month">{t('dashboard.filters.byMonth')}</option>
          <option value="custom">{t('dashboard.filters.customPeriod')}</option>
        </select>

        {modeValue === 'day' && (
          <input
            type="date"
            name={dayName}
            value={filters[dayName]}
            onChange={handleFilterChange}
            className={`${popoverControlClassName} w-full`}
          />
        )}

        {modeValue === 'month' && (
          <input
            type="month"
            name={monthName}
            value={filters[monthName]}
            onChange={handleFilterChange}
            className={`${popoverControlClassName} w-full`}
          />
        )}

        {modeValue === 'custom' && (
          <div className="grid grid-cols-2 gap-2">
            <input
              type="date"
              name={fromName}
              value={filters[fromName]}
              onChange={handleFilterChange}
              aria-label={t('dashboard.filters.fromDate')}
              className={`${popoverControlClassName} w-full`}
            />
            <input
              type="date"
              name={toName}
              value={filters[toName]}
              onChange={handleFilterChange}
              aria-label={t('dashboard.filters.toDate')}
              className={`${popoverControlClassName} w-full`}
            />
          </div>
        )}
      </div>
    );
  };

  if (loading && translations.length === 0) {
    return <Spinner />;
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-4">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
        <h1 className="text-2xl text-blue-800 mb-0">{t('dashboard.title')}</h1>
        <div className="flex flex-wrap gap-3 text-blue-800">
          <button 
            className="flex items-center gap-2 px-4 py-2 bg-green-300 rounded-lg hover:bg-green-400 transition-colors font-medium"
            onClick={handleCreateNewProject}
          >
            {t('dashboard.actions.newProject')}
          </button>
          <button
            className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors font-medium"
            onClick={handleImportTranslation}
          >
            Import Translation
          </button>
          
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200 mb-4">
        <button
          className={`px-2 py-3 text-sm font-medium transition-colors ${
            activeTab === 'history'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-gray-800'
          }`}
          onClick={() => handleTabChange('history')}
        >
          {t('dashboard.tabs.history')}
        </button>
        
      </div>
      
      {/* Filters */}
      <div className="bg-gray-50 rounded-lg p-3 mb-4">
        <div className="relative" ref={filtersPopoverRef}>
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            {activeTab === 'history' ? (
              <div className={quickFilterContainerClass(activeAdvancedFilterCount > 0)}>
                <button
                  type="button"
                  onClick={() => setIsFiltersPopoverOpen((prev) => !prev)}
                  className="h-8 inline-flex items-center gap-2 px-2 text-sm font-medium text-gray-800 rounded-full hover:bg-black/5"
                >
                  <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M7 12h10M10 18h4" />
                  </svg>
                  <span>{t('dashboard.filters.filtersButton')}{activeAdvancedFilterCount > 0 ? ` (${activeAdvancedFilterCount})` : ''}</span>
                </button>
              </div>
            ) : null}

            <div className={quickFilterContainerClass(Boolean(filters.sourceLanguage))}>
              <span className="text-xs font-semibold text-gray-600">{t('dashboard.filters.sourceLanguage')}</span>
              <select
                id="sourceLanguage"
                name="sourceLanguage"
                value={filters.sourceLanguage}
                onChange={handleFilterChange}
                className={`${compactControlClassName} min-w-[130px]`}
              >
                <option value="">{t('dashboard.filters.all')}</option>
                <option value="en">English</option>
                <option value="fr">French</option>
                <option value="es">Spanish</option>
                <option value="pt">Portuguese</option>
                <option value="it">Italian</option>
              </select>
            </div>

            <div className={quickFilterContainerClass(Boolean(filters.targetLanguage))}>
              <span className="text-xs font-semibold text-gray-600">{t('dashboard.filters.targetLanguage')}</span>
              <select
                id="targetLanguage"
                name="targetLanguage"
                value={filters.targetLanguage}
                onChange={handleFilterChange}
                className={`${compactControlClassName} min-w-[130px]`}
              >
                <option value="">{t('dashboard.filters.all')}</option>
                <option value="en">English</option>
                <option value="fr">French</option>
                <option value="es">Spanish</option>
                <option value="pt">Portuguese</option>
                <option value="it">Italian</option>
              </select>
            </div>

            {activeTab === 'history' ? (
              <>
                <div className={quickFilterContainerClass(Boolean(filters.approvalStatus))}>
                  <span className="text-xs font-semibold text-gray-600">{t('dashboard.filters.approvalStatus')}</span>
                  <select
                    id="approvalStatus"
                    name="approvalStatus"
                    value={filters.approvalStatus}
                    onChange={handleFilterChange}
                    className={`${compactControlClassName} min-w-[190px]`}
                  >
                    <option value="">{t('dashboard.filters.all')}</option>
                    <option value="pending_sending_for_approval">{t('dashboard.filters.pendingSendingForApproval')}</option>
                    <option value="pending_client_approval">{t('dashboard.filters.pendingClientApproval')}</option>
                    <option value="changes_requested">{t('dashboard.filters.changesRequested')}</option>
                  </select>
                </div>

                <div className={quickFilterContainerClass(Boolean(normalizeFilterText(filters.projectManager)))}>
                  <span className="text-xs font-semibold text-gray-600">{t('dashboard.filters.projectManager')}</span>
                  <input
                    id="projectManagerQuick"
                    name="projectManager"
                    value={filters.projectManager}
                    onChange={handleFilterChange}
                    type="text"
                    placeholder={t('dashboard.filters.projectManagerPlaceholder')}
                    className={`${compactControlClassName} min-w-[170px]`}
                  />
                </div>
              </>
            ) : null}
          </div>

          {activeTab === 'history' && activeAdvancedFilters.length > 0 ? (
            <div className="flex flex-wrap gap-2 mt-3">
              {activeAdvancedFilters.map((activeFilter) => (
                <div key={activeFilter.key} className="inline-flex items-center gap-2 px-3 py-1.5 text-xs bg-blue-50 text-blue-800 border border-blue-200 rounded-full">
                  <span className="font-semibold">{activeFilter.label}:</span>
                  <span>{activeFilter.value}</span>
                  <button
                    type="button"
                    onClick={() => clearAdvancedFilter(activeFilter.key)}
                    className="text-blue-700 hover:text-blue-900 font-bold leading-none"
                    aria-label={`Remove ${activeFilter.label} filter`}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          ) : null}

          {activeTab === 'history' && isFiltersPopoverOpen ? (
            <div className="absolute left-0 mt-2 z-20 w-[680px] max-w-[95vw] rounded-lg border border-gray-200 bg-white shadow-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-800">{t('dashboard.filters.filtersButton')}</h3>
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="text-sm font-medium text-blue-700 hover:text-blue-900"
                >
                  {t('dashboard.filters.clearAll')}
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('dashboard.filters.sourceLanguage')}</label>
                  <select
                    name="sourceLanguage"
                    value={filters.sourceLanguage}
                    onChange={handleFilterChange}
                    className={`${popoverControlClassName} w-full`}
                  >
                    <option value="">{t('dashboard.filters.all')}</option>
                    <option value="en">English</option>
                    <option value="fr">French</option>
                    <option value="es">Spanish</option>
                    <option value="pt">Portuguese</option>
                    <option value="it">Italian</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('dashboard.filters.targetLanguage')}</label>
                  <select
                    name="targetLanguage"
                    value={filters.targetLanguage}
                    onChange={handleFilterChange}
                    className={`${popoverControlClassName} w-full`}
                  >
                    <option value="">{t('dashboard.filters.all')}</option>
                    <option value="en">English</option>
                    <option value="fr">French</option>
                    <option value="es">Spanish</option>
                    <option value="pt">Portuguese</option>
                    <option value="it">Italian</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('dashboard.filters.approvalStatus')}</label>
                  <select
                    name="approvalStatus"
                    value={filters.approvalStatus}
                    onChange={handleFilterChange}
                    className={`${popoverControlClassName} w-full`}
                  >
                    <option value="">{t('dashboard.filters.all')}</option>
                    <option value="pending_sending_for_approval">{t('dashboard.filters.pendingSendingForApproval')}</option>
                    <option value="pending_client_approval">{t('dashboard.filters.pendingClientApproval')}</option>
                    <option value="changes_requested">{t('dashboard.filters.changesRequested')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('dashboard.filters.projectManager')}</label>
                  <input
                    name="projectManager"
                    value={filters.projectManager}
                    onChange={handleFilterChange}
                    type="text"
                    placeholder={t('dashboard.filters.projectManagerPlaceholder')}
                    className={`${popoverControlClassName} w-full`}
                  />
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('dashboard.filters.projectStatus')}</label>
                    <select
                      name="projectStatus"
                      value={filters.projectStatus}
                      onChange={handleFilterChange}
                      className={`${popoverControlClassName} w-full`}
                    >
                      <option value="">{t('dashboard.filters.all')}</option>
                      <option value="completed">{t('dashboard.status.completed')}</option>
                      <option value="overdue">{t('dashboard.status.overdue')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('dashboard.filters.status')}</label>
                    <select
                      name="status"
                      value={filters.status}
                      onChange={handleFilterChange}
                      className={`${popoverControlClassName} w-full`}
                    >
                      <option value="">{t('dashboard.filters.all')}</option>
                      <option value="PENDING">{t('dashboard.status.pending')}</option>
                      <option value="QUEUE">Queue</option>
                      <option value="PROCESSING">{t('dashboard.status.processing')}</option>
                      <option value="COMPLETED">{t('dashboard.status.completed')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('dashboard.filters.clientName')}</label>
                    <input
                      name="clientName"
                      value={filters.clientName}
                      onChange={handleFilterChange}
                      type="text"
                      placeholder={t('dashboard.filters.clientNamePlaceholder')}
                      className={`${popoverControlClassName} w-full`}
                    />
                  </div>
                  {renderDateFilterMenuControl('createdDate', 'dashboard.filters.createdDate')}
                  {renderDateFilterMenuControl('dueDate', 'dashboard.filters.dueDateFilter')}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      
      {/* Content based on active tab */}
      {activeTab === 'history' ? (
        <div className="space-y-4">
          {/* Projects Section */}
          {filteredProjects.length > 0 && (
            <div>
              <h2 className="text-md text-gray-800 mb-4">{t('dashboard.sections.myProjects')} ({filteredProjects.length})</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                {filteredProjects.map(project => {
                  const readyForApprovalStatus = getReadyForApprovalStatus(project);
                  const isDescriptionExpanded = Boolean(expandedProjectDescriptions[project.id]);
                  const {
                    previewText: projectDescriptionPreview,
                    isTruncated: isProjectDescriptionTruncated
                  } = getProjectDescriptionPreview(project.description);

                  return (
                  <a href={`/project/${project.id}`} key={project.id} className="bg-gray-50 rounded-lg px-4 py-4 border border-gray-200 hover:shadow-md transition-shadow relative group">
                    {/* Delete Icon - Shows on hover */}
                    <button
                      onClick={(e) => handleDeleteProject(project, e)}
                      className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity p-2 hover:bg-red-100 rounded-full"
                      title={t('common.delete')}
                    >
                      <svg
                        className="w-5 h-5 text-red-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>

                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-gray-800 pr-8">{project.name}</h3>
                      {project.status && (
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          project.status === 'completed' ? 'bg-green-100 text-green-800' :
                          project.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {project.status.replace('_', ' ')}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-3 whitespace-pre-line" title={project.description || ''}>
                      {isDescriptionExpanded ? (project.description || '') : projectDescriptionPreview}
                      {isProjectDescriptionTruncated && (
                        <>
                          {!isDescriptionExpanded && '... '}
                          <button
                            type="button"
                            onClick={(e) => toggleProjectDescription(project.id, e)}
                            className="ml-1 text-blue-500 hover:text-blue-600 cursor-pointer font-medium"
                          >
                            {isDescriptionExpanded ? 'See Less' : 'See More'}
                          </button>
                        </>
                      )}
                    </p>
                    
                    {/* Language Pair */}
                    {project.sourceLanguage && project.targetLanguage && (
                      <div className="flex items-center gap-1 text-sm text-gray-600 mb-2">
                        <span className="text-gray-400">🌐</span>
                        <span>
                          {getLanguageName(project.sourceLanguage)} → {getLanguageName(project.targetLanguage)}
                        </span>
                      </div>
                    )}
                    
                    {/* Completion Status */}
                    <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                      <div className="flex items-center gap-1 min-w-0 flex-1">
                        <span className="text-gray-400 flex-shrink-0">📊</span>
                        <span className="truncate" title={`${getProjectCompletionStatus(project).completed}/${getProjectCompletionStatus(project).total} ${t('dashboard.status.completed')}`}>
                          {getProjectCompletionStatus(project).completed}/{getProjectCompletionStatus(project).total}
                        </span>
                        <span className={`flex-shrink-0 ${
                          getProjectCompletionStatus(project).percentage === 100 ? 'text-green-600' :
                          getProjectCompletionStatus(project).percentage >= 50 ? 'text-blue-600' :
                          'text-orange-600'
                        }`}>
                          ({getProjectCompletionStatus(project).percentage}%)
                        </span>
                      </div>
                      <span className="text-gray-500 text-xs flex-shrink-0 ml-2">{new Date(project.createdAt).toLocaleDateString()}</span>
                    </div>

                    {/* Ready for Approval Status - show only when there is at least one item ready */}
                    {readyForApprovalStatus.ready > 0 && (
                      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-2 bg-red-100 border border-red-200 rounded-full text-xs font-medium">
                        <span className="truncate font-medium" title={`${readyForApprovalStatus.ready}/${readyForApprovalStatus.total} ${t('dashboard.readyForApproval')}`}>
                          {readyForApprovalStatus.ready}/{readyForApprovalStatus.total}
                        </span>
                        <span className="flex-shrink-0 font-medium">
                          {t('dashboard.readyForApproval')}
                        </span>
                      </div>
                    )}

                    {project.dueDate && (
                      <div className="flex items-center justify-between text-sm mb-2 gap-2">
                        <div className="flex items-center gap-1 min-w-0 flex-1">
                          <span className="text-gray-400 flex-shrink-0">📅</span>
                          <span className="text-gray-500 truncate" title={`${t('dashboard.table.dueDate')}: ${new Date(project.dueDate).toLocaleDateString()}`}>
                            {new Date(project.dueDate).toLocaleDateString()}
                          </span>
                        </div>
                        {getDueDateStatus(project)?.message && (
                          <span
                            className={`px-2 py-1 text-xs flex-shrink-0 rounded-full whitespace-nowrap ${
                              getDueDateStatus(project)?.status === 'overdue' ? 'bg-red-100 text-red-700' :
                              getDueDateStatus(project)?.status === 'urgent' ? 'bg-red-50 text-red-600' :
                              getDueDateStatus(project)?.status === 'warning' ? 'bg-orange-50 text-orange-600' :
                              'bg-green-50 text-green-600'
                            }`}
                            title={getDueDateStatus(project)?.message}
                          >
                            {getDueDateStatus(project)?.message}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="mt-4 flex gap-2">
                      <Link
                        to={`/project/${project.id}`}
                        className="flex-1 text-center px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors font-medium"
                      >
                        {t('common.view')}
                      </Link>
                      <Link
                        to={`/translator/setup?projectId=${project.id}`}
                        className="flex-2 text-center px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors font-medium"
                      >
                        {t('dashboard.actions.uploadDocument')}
                      </Link>
                    </div>
                  </a>
                );
                })}
              </div>
            </div>
          )}

          {/* Translations Section */}
          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-4">{t('dashboard.sections.myTranslations')} ({filteredTranslations.length})</h2>
            {filteredTranslations.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-12 bg-gray-50 rounded-lg text-center">
                {translations.length === 0 ? (
                  <>
                    <p className="text-gray-600 mb-4">{t('dashboard.emptyState.noTranslations')}</p>
                    <div className="flex gap-3">
                      <button
                        className="px-4 py-2 bg-green-300 rounded-lg hover:bg-green-400 transition-colors font-medium"
                        onClick={handleCreateNewProject}
                      >
                        {t('dashboard.actions.startWithProject')}
                      </button>
                      <button
                        className="px-4 py-2 bg-blue-300 rounded-lg hover:bg-blue-400 transition-colors font-medium"
                        onClick={handleCreateNewTranslation}
                      >
                        {t('dashboard.actions.singleTranslation')}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-gray-600 mb-2">{t('dashboard.emptyState.noMatches')}</p>
                    <p className="text-sm text-gray-500">{t('dashboard.emptyState.adjustFilters')}</p>
                  </>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg shadow-md">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-200">{t('dashboard.table.project')}</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-200">{t('dashboard.table.fileName')}</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-200">{t('dashboard.filters.sourceLanguage')} → {t('dashboard.filters.targetLanguage')}</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-200">{t('dashboard.table.dueDate')}</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-200">{t('dashboard.table.status')}</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-200">{t('dashboard.table.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTranslations.map(translation => {
                      const isClickable = translation.status === 'PENDING' || translation.status === 'COMPLETED';
                      return (
                        <tr
                          key={translation.id}
                          onClick={() => isClickable && handleTranslationRowClick(translation)}
                          className={`transition-colors ${
                            isClickable
                              ? 'hover:bg-blue-50 cursor-pointer'
                              : 'hover:bg-gray-50'
                          }`}
                          title={
                            translation.status === 'PENDING' ? 'Click to start translation' :
                            translation.status === 'COMPLETED' ? 'Click to edit translation' :
                            ''
                          }
                        >
                          <td className="px-4 py-3 text-sm text-gray-900 border-b border-gray-200">
                            {translation.project?.name || projectsById[translation.projectId]?.name || t('dashboard.table.noProject')}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 border-b border-gray-200">{translation.originalFileName || 'Untitled Document'}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 border-b border-gray-200">
                            {translation.sourceLanguage} → {translation.targetLanguage}
                          </td>

                          <td className="px-4 py-3 text-sm text-gray-900 border-b border-gray-200">
                            {new Date(translation.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-sm border-b border-gray-200">
                            <span className={`inline-flex items-center px-3 py-1 text-xs font-bold uppercase rounded-full tracking-wide ${
                              translation.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                              translation.status === 'QUEUE' ? 'bg-blue-100 text-blue-800' :
                              translation.status === 'PROCESSING' ? 'bg-blue-100 text-blue-800' :
                              translation.status === 'ready_for_review' ? 'bg-purple-100 text-purple-800' :
                              translation.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {translation.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm border-b border-gray-200">
                            <button
                              onClick={(e) => handleDeleteTranslation(translation, e)}
                              className="px-3 py-1 text-sm bg-red-100 text-red-800 rounded hover:bg-red-200 transition-colors font-medium"
                              title={t('dashboard.actionButtons.delete')}
                            >
                              {t('dashboard.actionButtons.delete')}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Available Translations Tab */
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Available Translations</h2>
          {translations.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12 bg-gray-50 rounded-lg text-center">
              <p className="text-gray-600 mb-4">No available translations found matching your filters.</p>
              <p className="text-sm text-gray-500">Check back later for new translation opportunities.</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg shadow-md">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-200">{t('dashboard.table.documentType')}</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-200">{t('dashboard.table.documentHolder')}</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-200">{t('dashboard.table.sourceLanguage')}</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-200">{t('dashboard.table.targetLanguage')}</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-200">{t('dashboard.table.submittedDate')}</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-200">{t('dashboard.table.status')}</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-200">{t('dashboard.table.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {translations.map(translation => (
                    <tr key={translation.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-gray-900 border-b border-gray-200">{translation.documentType}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 border-b border-gray-200">{translation.documentHolder}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 border-b border-gray-200">{translation.sourceLanguage}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 border-b border-gray-200">{translation.targetLanguage}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 border-b border-gray-200">{new Date(translation.createdAt).toLocaleDateString()}</td>
                      <td className="px-4 py-3 text-sm border-b border-gray-200">
                        <span className={`inline-flex items-center px-3 py-1 text-xs font-bold uppercase rounded-full tracking-wide ${
                          translation.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          translation.status === 'claimed' ? 'bg-blue-100 text-blue-800' :
                          translation.status === 'translation_in_progress' ? 'bg-green-100 text-green-800' :
                          translation.status === 'ready_for_review' ? 'bg-purple-100 text-purple-800' :
                          translation.status === 'completed' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {translation.status === 'pending' && t('dashboard.status.pending')}
                          {translation.status === 'claimed' && t('dashboard.status.claimed')}
                          {translation.status === 'translation_in_progress' && t('dashboard.status.translationInProgress')}
                          {translation.status === 'ready_for_review' && t('dashboard.status.readyForReview')}
                          {translation.status === 'completed' && t('dashboard.status.completed')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm border-b border-gray-200">
                        {translation.status === 'pending' && (
                          <button 
                            className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed" 
                            onClick={() => handleClaimTranslation(translation.id)}
                            disabled={claimingId === translation.id}
                          >
                            {claimingId === translation.id ? t('dashboard.actionButtons.claiming') : t('dashboard.actionButtons.claim')}
                          </button>
                        )}
                        {translation.status === 'claimed' && 
                          translation.assignedTranslator === 'current_user' && (
                          <Link 
                            to={`/translator/setup/${translation.id}`} 
                            className="inline-block px-3 py-1 text-sm bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors font-medium no-underline"
                          >
                            {t('dashboard.actionButtons.setUp')}
                          </Link>
                        )}
                        {translation.status === 'translation_in_progress' &&
                          translation.assignedTranslator === 'current_user' && (
                          <button
                            className="px-3 py-1 text-sm bg-gray-200 text-gray-600 rounded cursor-not-allowed font-medium opacity-50"
                            disabled
                          >
                            {t('dashboard.actionButtons.processingDots')}
                          </button>
                        )}
                        {translation.status === 'ready_for_review' &&
                          translation.assignedTranslator === 'current_user' && (
                          <Link
                            to={`/translator/edit/${translation.id}?mode=googledocs`}
                            className="inline-block px-3 py-1 text-sm bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors font-medium no-underline"
                          >
                            {t('dashboard.actionButtons.review')}
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Delete Translation Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={cancelDeleteTranslation}
        onConfirm={confirmDeleteTranslation}
        title={t('dashboard.deleteDialog.title')}
        message={t('dashboard.deleteDialog.message')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        isLoading={isDeleting}
      />

      {/* Delete Project Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteProjectDialog.isOpen}
        onClose={cancelDeleteProject}
        onConfirm={confirmDeleteProject}
        title={t('dashboard.deleteDialog.title')}
        message={t('dashboard.deleteDialog.projectMessage')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        isLoading={isDeletingProject}
      />
    </div>
  );
};

export default TranslatorDashboard;
